// 导入 crypto-js 包
import CryptoJS from "crypto-js"

/**
 * 定义加密函数
 * @param {string} data - 需要加密的数据, 传过来前先进行 JSON.stringify(data);
 * @param {string} initKey - 加密使用的 key
 */
const aesEncrypt = (data: string, initKey: string): string => {
    // 获取填充后的key
    const key = CryptoJS.enc.Utf8.parse(initKey)

    const encData = CryptoJS.enc.Utf8.parse(data)

    /**
     * CipherOption, 加密的一些选项:
     *   mode: 加密模式, 可取值(CBC, CFB, CTR, CTRGladman, OFB, ECB), 都在 CryptoJS.mode 对象下
     *   padding: 填充方式, 可取值(Pkcs7, AnsiX923, Iso10126, Iso97971, ZeroPadding, NoPadding), 都在 CryptoJS.pad 对象下
     *   iv: 偏移量, mode === ECB 时, 不需要 iv
     * 返回的是一个加密对象
     */

    const cipher = CryptoJS.AES.encrypt(encData, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
    })

    return CryptoJS.enc.Hex.stringify(cipher.ciphertext)
}

/**
 * 定义解密函数
 * @param {string} encryptedData - 需要解密的数据
 * @param {string} initKey - 解密使用的 key
 */
const aesDecrypt = (encryptedData: string, initKey: string): string => {
    // 获取填充后的key
    const key = CryptoJS.enc.Utf8.parse(initKey)

    // 将十六进制字符串转为 WordArray 对象
    const encryptedHexStr = CryptoJS.enc.Hex.parse(encryptedData)
    const encryptedBase64Str = CryptoJS.enc.Base64.stringify(encryptedHexStr)

    // 解密
    const decrypt = CryptoJS.AES.decrypt(encryptedBase64Str, key, {
        mode: CryptoJS.mode.ECB,
        padding: CryptoJS.pad.Pkcs7,
    })

    return CryptoJS.enc.Utf8.stringify(decrypt).toString()
}

function sha512Encrypt(data: string): string {
    const hash = CryptoJS.SHA512(data)
    const result = CryptoJS.enc.Hex.stringify(hash)
    return result
}

function randomResult(len?: number): string {
    const str = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890"
    const arr = str.split("")
    let result = ""
    for (let i = 0; i < (len || 4); i += 1) {
        const n = Math.floor(Math.random() * arr.length)
        result += arr[n]
    }
    return result
}

function sortParams(form: Record<string, any>): string {
    const keys = Object.keys(form)
    const arr: any[] = []
    for (let i = 0; i < keys.length; i += 1) {
        arr.push(form[keys[i]])
    }

    const sortArr = arr.sort()
    let sortedParams = ""
    for (let i = 0; i < sortArr.length; i += 1) {
        sortedParams += sortArr[i]
    }
    return sortedParams
}

// 用于处理query的加密和解密
// 添加用于加密和解密的工具函数
export const ENCRYPTED_FLAG = "e"
// key的长度必须是16, 24, 32位，否则会报错, query加密的安全等级比较低，key先hardcode在这里，后续如果有需要，考虑放到环境变量中，由外部统一保管
const QUERY_ENCRYPTION_KEY = "crm-query-?2024!" // 用于query加密的密钥

// 使用AES加密查询参数
export function encryptQuery(query: Record<string, any>): Record<string, any> {
    if (!query || Object.keys(query).length === 0) return query

    // 如果已经加密过，直接返回
    if (query[ENCRYPTED_FLAG]) return query

    const encryptedQuery: Record<string, any> = {}

    for (const key in query) {
        if (Object.hasOwn(query, key)) {
            try {
                const value = query[key]
                // 目前存在第三方携带assetToken跳转至项目的场景，当遇到assetToken时，不做加密处理
                if (
                    key !== "assetToken" &&
                    value !== undefined &&
                    value !== null
                ) {
                    const stringValue =
                        typeof value === "object"
                            ? JSON.stringify(value)
                            : String(value)
                    encryptedQuery[key] = aesEncrypt(
                        stringValue,
                        QUERY_ENCRYPTION_KEY
                    )
                } else {
                    encryptedQuery[key] = value
                }
            } catch (e) {
                encryptedQuery[key] = query[key]
            }
        }
    }

    // Add the flag at the end
    encryptedQuery[ENCRYPTED_FLAG] = "t"

    return encryptedQuery
}

// 解密函数
export function decryptQuery(query: Record<string, any>): Record<string, any> {
    // 处理边界情况
    if (!query || !query[ENCRYPTED_FLAG]) return query

    const decryptedQuery: Record<string, any> = {}

    // 遍历查询参数
    Object.keys(query).forEach((key) => {
        // 跳过加密标记
        if (key === ENCRYPTED_FLAG) return

        const value = query[key]

        // 如果不是字符串或为空，直接保留原值
        // 如果是assetToken，直接保留原值
        if (key === "assetToken" || typeof value !== "string" || !value) {
            decryptedQuery[key] = value
            return
        }

        try {
            // 解密值
            const decryptedValue = aesDecrypt(value, QUERY_ENCRYPTION_KEY)

            // 尝试解析为JSON对象
            try {
                const parsedValue = JSON.parse(decryptedValue)
                decryptedQuery[key] =
                    typeof parsedValue === "object"
                        ? parsedValue
                        : decryptedValue
            } catch {
                // 不是有效JSON，保持字符串形式
                decryptedQuery[key] = decryptedValue
            }
        } catch {
            // 解密失败，保留原值
            decryptedQuery[key] = value
        }
    })

    return decryptedQuery
}

export default {
    aesEncrypt,
    aesDecrypt,
    sha512Encrypt,
    randomResult,
    sortParams,
}
